import React, { useRef, useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FiPhone,
  FiMail,
  FiMapPin,
  FiClock,
  FiUser,
  FiHeadphones,
} from "react-icons/fi";
import { ThemeContext } from "../context/ThemeContext";

function Contact() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const { theme } = useContext(ThemeContext);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section
      className={`${theme === "dark" ? "dark" : ""
        } bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800 mt-32 mb-20 pt-6`}
      id="contact"
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{
            hidden: { opacity: 0, scale: 0.95 },
            visible: {
              opacity: 1,
              scale: 1,
              transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.15, delayChildren: 0.2 },
            },
          }}
        >

          {/* Contact Info */}
          <motion.div variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }} className="mb-16">
            <motion.h2
              className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-6 sm:mb-10 md:mb-12 bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent px-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Get In Touch With{" "}
              <span className="text-[#007bff]">Tripocation Holiday Pvt. Ltd.</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-10 gap-x-6">
              {[
                {
                  title: "Founder",
                  name: "Prince Kumar",
                  email: "prince@tripocationholiday.com",
                  icon: <FiUser />,
                },
                {
                  title: "Co-Founder",
                  name: "Sachin Kumar",
                  email: "sachin@tripocationholiday.com",
                  icon: <FiUser />,
                },
                {
                  title: "Phone",
                  content: "+91 92201 98202",
                  icon: <FiPhone />,
                  isLink: "tel:+919220198202",
                },
                {
                  title: "Office Address",
                  content: (
                    <>
                      B-3/58, Second Floor
                      <br />
                      Sector-6, Rohini
                      <br />
                      New Delhi - 110085
                    </>
                  ),
                  icon: <FiMapPin />,
                },
                {
                  title: "Working Hours",
                  content: (
                    <>
                      <strong>Monday - Saturday:</strong> 10AM - 9PM
                      <br />
                      <strong>Sunday:</strong> Closed
                    </>
                  ),
                  icon: <FiClock />,
                },
                {
                  title: "24/7 Support",
                  content:
                    "Our team is available 24/7 to assist you anytime, anywhere. Feel free to contact us whenever you need help.",
                  icon: <FiHeadphones />,
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700 relative overflow-hidden group"
                >
                  <div className="absolute -right-6 -top-6 w-24 h-24 bg-blue-100 dark:bg-gray-700 rounded-full opacity-20 group-hover:opacity-30 transition-all duration-300"></div>
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-100 dark:bg-gray-700 rounded-lg text-blue-600 dark:text-blue-400">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-semibold mb-1">
                        {item.title}
                      </h3>
                      {item.name && (
                        <p className="text-sm md:text-base font-medium mb-1">
                          {item.name}
                        </p>
                      )}
                      {item.email && (
                        <a
                          href={`mailto:${item.email}`}
                          className="text-sm break-all text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {item.email}
                        </a>
                      )}
                      {item.isLink ? (
                        <a
                          href={item.isLink}
                          className="block text-sm md:text-base text-blue-600 dark:text-blue-400 hover:underline mt-1"
                        >
                          {item.content}
                        </a>
                      ) : (
                        item.content && (
                          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">
                            {item.content}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Google Map */}
          <motion.div
            className="mb-16 rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700"
          >
            <iframe
              title="Tripocation Holiday Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3499.876123456789!2d77.123456!3d28.654321!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM5JzE1LjYiTiA3N8KwMDcnMjEuNiJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-xl"
            ></iframe>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="grid md:grid-cols-2">
              {/* Left Side */}
              <div className="hidden md:block bg-gradient-to-br from-blue-500 to-cyan-400 p-12 relative">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f')] bg-cover bg-center opacity-20"></div>
                <div className="relative z-10 text-white">
                  <h3 className="text-2xl font-bold mb-4">
                    Let's Start Your Journey
                  </h3>
                  <p className="mb-6 opacity-90">
                    We're here to help you plan your perfect holiday. Reach out
                    to us with any questions or special requests.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <FiPhone className="w-5 h-5" />
                      <span>+91 92201 98202</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <FiMail className="w-5 h-5" />
                      <span>prince@tripocationholiday.com</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="p-8 md:p-12">
                <h3 className="text-2xl font-bold mb-6">Send us a message</h3>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input type="text" placeholder="First Name" className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-gray-700 dark:text-white" />
                    <input type="text" placeholder="Last Name" className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input type="email" placeholder="Email" className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-gray-700 dark:text-white" />
                    <input type="text" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-gray-700 dark:text-white" />
                  </div>
                  <input type="text" placeholder="Subject" className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-gray-700 dark:text-white" />
                  <textarea rows="4" placeholder="Your message..." className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-gray-700 dark:text-white"></textarea>
                  <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Contact;
